<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose:
                'Ten plik cookie służy do rozróżniania ludzi i botów. Jest to korzystne dla strony, aby tworzyć prawidłowe raporty dotyczące korzystania ze strony.',
            expiry: '180 dni',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: 'Przechowuje stan zgody użytkownika na pliki cookie dla bieżącej domeny.',
            expiry: '1 rok',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose: 'Używany przez system zarządzania treścią (CMS) strony do określania, jak wyświetlać zakładki menu.',
            expiry: 'Stały',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose: 'Używany przez CMS strony do określania, jak wyświetlać zakładki menu.',
            expiry: 'Stały',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose: 'Służy do rozróżniania ludzi i botów. Korzystne dla strony w celu raportowania korzystania.',
            expiry: 'Stały',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'Służy do rozróżniania ludzi i botów.',
            expiry: 'Stały',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'Służy do rozróżniania ludzi i botów.',
            expiry: 'Stały',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'Służy do rozróżniania ludzi i botów.',
            expiry: 'Stały',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose: 'Określa preferowany język odwiedzającego. Pozwala stronie ustawić preferowany język przy ponownym wejściu użytkownika.',
            expiry: '1 rok',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose:
                'Służy do przesyłania danych do Google Analytics o urządzeniu i zachowaniu odwiedzającego. Śledzi użytkownika na różnych urządzeniach i kanałach marketingowych.',
            expiry: '2 lata',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose:
                'Służy do przesyłania danych do Google Analytics o urządzeniu i zachowaniu odwiedzającego. Śledzi użytkownika na różnych urządzeniach i kanałach marketingowych.',
            expiry: '2 lata',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Określa preferowany język gościa.',
            expiry: 'Stały',
            type: 'HTML Local Storage',
        },
    ],
};
</script>
<template>
    <ContentPage title="Polityka plików cookie">
        <p>
            Ta strona używa plików cookie. Używamy plików cookie, aby personalizować treści i reklamy, zapewniać funkcje mediów społecznościowych oraz
            analizować ruch na naszej stronie. Udostępniamy również informacje o korzystaniu z naszej witryny naszym partnerom z zakresu mediów
            społecznościowych, reklamy i analityki, którzy mogą łączyć je z innymi informacjami, które im przekazałeś lub które zebrali podczas
            korzystania z ich usług.
        </p>
        <p>
            Pliki cookie to małe pliki tekstowe, które mogą być używane przez strony internetowe, aby uczynić korzystanie z nich bardziej efektywnym.
        </p>
        <p>
            Prawo stanowi, że możemy przechowywać pliki cookie na Twoim urządzeniu, jeśli są one absolutnie niezbędne do działania tej strony. W
            przypadku wszystkich innych rodzajów plików cookie potrzebujemy Twojej zgody.
        </p>
        <p>
            Ta strona używa różnych typów plików cookie. Niektóre pliki cookie są umieszczane przez usługi stron trzecich, które pojawiają się na
            naszych stronach.
        </p>
        <p>W każdej chwili możesz zmienić lub wycofać swoją zgodę z Deklaracji plików cookie na naszej stronie.</p>
        <p>
            Dowiedz się więcej o tym, kim jesteśmy, jak się z nami skontaktować i jak przetwarzamy dane osobowe w naszej
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Polityce prywatności</NuxtLinkLocale>.
        </p>
        <p>Podając zapytanie dotyczące swojej zgody, prosimy o podanie identyfikatora zgody oraz daty.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Niezbędne (8)</h3>
            <p>
                Niezbędne pliki cookie pomagają uczynić stronę użyteczną, umożliwiając podstawowe funkcje, takie jak nawigacja po stronie i dostęp do
                jej bezpiecznych obszarów. Strona nie może funkcjonować poprawnie bez tych plików cookie.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Preferencje (1)</h3>
            <p>
                Pliki cookie preferencji pozwalają stronie zapamiętać informacje, które zmieniają sposób działania lub wyglądu strony, jak preferowany
                język lub region, w którym się znajdujesz.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Marketing (2)</h3>
            <p>
                Pliki cookie marketingowe są używane do śledzenia odwiedzających na stronach internetowych. Celem jest wyświetlanie reklam, które są
                istotne i angażujące dla indywidualnego użytkownika, a tym samym bardziej wartościowe dla wydawców i reklamodawców stron trzecich.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Niesklasyfikowane (1)</h3>
            <p>Niesklasyfikowane pliki cookie to takie, które są w trakcie klasyfikacji wraz z dostawcami tych plików.</p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
